import { useTranslation } from 'react-i18next';
import type { PageType } from 'types';
import type { PopularProductsModule } from 'types/capi/modules';

import CarouselScroller from '~/components/CarouselScroller';
import ProductCard from '~/components/ProductCard';
import Tracked from '~/components/Tracked';
import { MODULE_CTA_POSITION } from '~/events/NavigationLinkInteractionEvent';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';

const MODULE_TYPE = 'homepage-popular-products';

type Props = {
  items: PopularProductsModule['items'];
  modulePosition: number;
  pageType: PageType;
};

function ProductsCarousel({ items, modulePosition, pageType }: Props) {
  return (
    <CarouselScroller slidesClassName="gap-2 md:gap-6">
      {items.map((product, productIndex) => {
        const baseProps = {
          allowNotifications: true,
          cardSize: 'small' as const,
          className: 'w-[42vw] flex-none md:w-[264px]',
          imageLoading: 'eager' as const,
          modulePosition,
          moduleType: MODULE_TYPE,
          overrideInteractionPagePosition: true,
          padImage: true,
          pagePosition: 'Homepage Whats Hot' as const,
          position: productIndex + 1,
          showNotificationCount: true
        };

        return product.type === 'external' ? (
          <ProductCard.External
            key={`product-${productIndex}`}
            product={product}
            pageType={pageType}
            {...baseProps}
          />
        ) : (
          <ProductCard.Internal
            key={`product-${productIndex}`}
            product={product}
            pageType={pageType}
            {...baseProps}
          />
        );
      })}
    </CarouselScroller>
  );
}

const VIEW_MORE_PATH = '/release-dates/';

function ViewMoreButton({ modulePosition }: { modulePosition: number }) {
  const { t } = useTranslation('common');
  const fireNavigationLink = useNavigationLinkInteraction('module_cta');
  const label = t('viewMore');

  return (
    <div className="w-36">
      <Tracked.InternalLink
        variant="transparent-black-border"
        className="flex items-center justify-center"
        pagePosition="Homepage Whats Hot"
        name="VIEW_MORE"
        to={VIEW_MORE_PATH}
        onClick={() =>
          fireNavigationLink(VIEW_MORE_PATH, MODULE_CTA_POSITION, {
            label,
            modulePosition,
            moduleType: MODULE_TYPE
          })
        }
      >
        {label}
      </Tracked.InternalLink>
    </div>
  );
}

export default function PopularProducts({
  items,
  modulePosition,
  pageType
}: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full">
      <div className="content-width flex flex-col items-center justify-center gap-6 py-10 md:gap-8 md:py-20">
        <div className="flex flex-col gap-6 self-stretch py-0 md:gap-10">
          <div className="flex flex-col gap-2">
            <h2 className="text-title-xl flex flex-col justify-start gap-2 text-left">
              {t('whatsHot')}
            </h2>
            <div className="text-card-sm">{t('whatsHotDescription')}</div>
          </div>
          <ProductsCarousel
            items={items}
            modulePosition={modulePosition}
            pageType={pageType}
          />
        </div>
        <ViewMoreButton modulePosition={modulePosition} />
      </div>
    </div>
  );
}
